/* Shared code with the cobot project */
export async function promiseAllWithConcurrency<T>(
  tasks: Array<() => Promise<T>>,
  concurrency: number = 8
): Promise<T[]> {
  console.log(`Starting promiseAllWithConcurrency with ${tasks.length} tasks and concurrency ${concurrency}`);
  
  // Input validation
  if (!Array.isArray(tasks)) {
    throw new Error('Tasks parameter must be an array');
  }
  
  if (typeof concurrency !== 'number' || concurrency <= 0 || !Number.isInteger(concurrency)) {
    throw new Error('Concurrency must be a positive integer');
  }
  
  let inProgress = 0;
  let index = 0;
  const results: (T | Error)[] = [];
  const errors: Error[] = [];

  return new Promise((resolve, reject) => {
    const process = async () => {
      while (inProgress < concurrency && index < tasks.length) {
        const currentIndex = index++;
        inProgress++;
        console.log(`Starting task ${currentIndex}, inProgress: ${inProgress}, remaining: ${tasks.length - index}`);

        (async () => {
          try {
            const task = tasks[currentIndex];
            console.log(`Executing task ${currentIndex}`);
            const result = await task();
            console.log(`Task ${currentIndex} completed successfully`);
            results[currentIndex] = result;
          } catch (error) {
            const typedError = error instanceof Error ? error : new Error(String(error));
            console.error(`Task ${currentIndex} failed:`, typedError);
            results[currentIndex] = typedError;
            errors.push(typedError);
          } finally {
            inProgress--;
            console.log(`Task ${currentIndex} finished, inProgress: ${inProgress}, errors: ${errors.length}`);
            if (index >= tasks.length && inProgress === 0) {
              if (errors.length > 0) {
                console.error(`All tasks completed with ${errors.length} errors, rejecting`);
                reject(errors);
              } else {
                console.log('All tasks completed successfully');
                resolve(results as T[]);
              }
            } else {
              process();
            }
          }
        })();
      }
    };

    process();
  });
}

/**
 * A generator function that executes promises with limited concurrency and yields results as they resolve.
 * Use this when you want to process results immediately without waiting for all promises to complete.
 *
 * @param tasks - Array of functions that return promises
 * @param concurrency - Maximum number of promises to execute simultaneously
 * @param onError - Optional callback function to handle errors
 * @yields {T} Results as they become available
 * 
 * Example usage:
 * ```
 * const generator = processPromisesWithConcurrency(myTasks, 3);
 * for await (const result of generator) {
 *   // Process each result as soon as it completes
 * }
 * ```
 */
export async function* processPromisesWithConcurrency<T>(
  tasks: Array<() => Promise<T>>,
  concurrency: number = 8,
  onError?: (error: Error, taskIndex: number) => void
): AsyncGenerator<T> {
  // Input validation
  if (!Array.isArray(tasks)) {
    throw new Error('Tasks parameter must be an array');
  }
  
  if (typeof concurrency !== 'number' || concurrency <= 0 || !Number.isInteger(concurrency)) {
    throw new Error('Concurrency must be a positive integer');
  }

  // Track active promises
  const active = new Map<number, Promise<{ index: number; result: T; } | { index: number; error: Error; }>>();
  let nextIndex = 0;

  // Helper to create a task with its index
  const createIndexedTask = (task: () => Promise<T>, index: number) => {
    return Promise.resolve()
      .then(() => task())
      .then(result => ({ index, result }))
      .catch(error => {
        const typedError = error instanceof Error ? error : new Error(String(error));
        if (onError) {
          onError(typedError, index);
        }
        return { index, error: typedError };
      });
  };

  // Start as many tasks as concurrency allows
  const startNextTasks = () => {
    while (active.size < concurrency && nextIndex < tasks.length) {
      const task = tasks[nextIndex];
      const taskPromise = createIndexedTask(task, nextIndex);
      active.set(nextIndex, taskPromise);
      nextIndex++;
    }
  };

  try {
    // Process tasks until all complete
    while (nextIndex < tasks.length || active.size > 0) {
      // Fill up to concurrency limit
      startNextTasks();
      
      if (active.size === 0) break;
      
      // Wait for the next task to complete
      const nextCompleted = await Promise.race(active.values());
      
      // Remove the completed task from active
      active.delete(nextCompleted.index);
      
      // Yield the result if successful
      if ('result' in nextCompleted) {
        yield nextCompleted.result;
      }
    }
  } finally {
    // Ensure any remaining tasks are cleaned up if the generator is terminated early
    active.clear();
  }
}
